import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '../ui/Switch';
import AppIcon from '../AppIcon';
import { cn } from '../../utils/cn';
import type {
  NotificationPreferenceView,
  CompanyNotificationSettingView,
} from '../../api/General/NotificationPreference';

// --- Constants ---

export const NOTIFICATION_CATEGORIES: Record<string, string[]> = {
  Attendance: [
    'ClockInNotification',
    'ClockOutNotification',
    'MissedClockInNotification',
    'EarlyClockOutNotification',
    'BreakInNotification',
    'BreakOutNotification',
  ],
  'Leave & Holidays': ['HolidayNotification', 'BlackoutNotification'],
  Payroll: ['PayslipNotification'],
  Compliance: ['CchiExpiryReminder'],
  Schedule: ['ScheduleChangedNotification', 'ShiftReminderNotification'],
  Requests: [
    'AttendanceCorrectionSubmittedNotification',
    'AttendanceCorrectionDecisionNotification',
  ],
  General: ['OnDemandNotification'],
};

export const HR_ADMIN_ONLY_TYPES = new Set([
  'CchiExpiryReminder',
]);

export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  ClockInNotification: 'Clock In Reminder',
  ClockOutNotification: 'Clock Out Reminder',
  MissedClockInNotification: 'Missed Clock In',
  EarlyClockOutNotification: 'Early Clock Out',
  BreakInNotification: 'Break In Reminder',
  BreakOutNotification: 'Break Out Reminder',
  HolidayNotification: 'Holiday Announcement',
  BlackoutNotification: 'Blackout Period',
  PayslipNotification: 'Payslip Generated',
  CchiExpiryReminder: 'CCHI Policy Expiry',
  ScheduleChangedNotification: 'Schedule Changed',
  ShiftReminderNotification: 'Shift Reminder',
  AttendanceCorrectionSubmittedNotification: 'Correction Submitted',
  AttendanceCorrectionDecisionNotification: 'Correction Decision',
  OnDemandNotification: 'Custom Notification',
};

// --- Types ---

interface NotificationPreferenceMatrixProps {
  /** User mode: show preferences with toggles */
  preferences?: NotificationPreferenceView[];
  onToggle?: (
    notificationTypeId: number,
    channel: 'email' | 'push' | 'inApp',
    value: boolean
  ) => void;

  /** Admin mode: show company settings with force-disable */
  companySettings?: CompanyNotificationSettingView[];
  onCompanySettingChange?: (
    notificationTypeId: number,
    field: string,
    value: boolean
  ) => void;

  /** Common */
  disabled?: boolean;
  readOnly?: boolean;
  mode: 'user' | 'admin';
  userRole?: string;
}

type Channel = 'email' | 'push' | 'inApp';

const CHANNELS: { key: Channel; label: string; masterLabel: string }[] = [
  { key: 'email', label: 'Email', masterLabel: 'All Email' },
  { key: 'push', label: 'Push', masterLabel: 'All Push' },
  { key: 'inApp', label: 'In-App', masterLabel: 'All In-App' },
];

// --- Helpers ---

/** Map admin field names to channel keys */
function adminFieldForChannel(channel: Channel): string {
  switch (channel) {
    case 'email':
      return 'emailsDisabled';
    case 'push':
      return 'mobilePushNotificationsDisabled';
    case 'inApp':
      return 'inAppDisabled';
  }
}

/** Organize items by category, putting unknowns into General */
function categorize<T extends { notificationTypeName: string }>(
  items: T[]
): { category: string; items: T[] }[] {
  const byName = new Map<string, T>();
  for (const item of items) {
    byName.set(item.notificationTypeName, item);
  }

  const result: { category: string; items: T[] }[] = [];

  for (const [category, typeNames] of Object.entries(NOTIFICATION_CATEGORIES)) {
    const categoryItems: T[] = [];
    for (const name of typeNames) {
      const item = byName.get(name);
      if (item) {
        categoryItems.push(item);
        byName.delete(name);
      }
    }
    if (categoryItems.length > 0) {
      result.push({ category, items: categoryItems });
    }
  }

  // Remaining items go to General
  if (byName.size > 0) {
    const generalIdx = result.findIndex((r) => r.category === 'General');
    const remaining = Array.from(byName.values());
    if (generalIdx >= 0) {
      result[generalIdx].items.push(...remaining);
    } else {
      result.push({ category: 'General', items: remaining });
    }
  }

  return result;
}

// --- Component ---

const NotificationPreferenceMatrix: React.FC<
  NotificationPreferenceMatrixProps
> = ({
  preferences,
  onToggle,
  companySettings,
  onCompanySettingChange,
  disabled = false,
  readOnly = false,
  mode,
  userRole,
}) => {
  const { t } = useTranslation();

  // Category collapse state - all expanded by default
  const [collapsedCategories, setCollapsedCategories] = useState<
    Set<string>
  >(new Set());

  const toggleCategory = useCallback((category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  // --- User mode helpers ---

  const showAllTypes = !userRole || userRole === 'Hr' || userRole === 'Admin';

  const userCategories = useMemo(() => {
    if (mode !== 'user' || !preferences) return [];
    const filtered = showAllTypes
      ? preferences
      : preferences.filter(p => !HR_ADMIN_ONLY_TYPES.has(p.notificationTypeName));
    return categorize(filtered);
  }, [mode, preferences, showAllTypes]);

  /** Compute master toggle state for user mode */
  const userMasterState = useMemo(() => {
    if (mode !== 'user' || !preferences) {
      return { email: false, push: false, inApp: false };
    }
    const channels: Record<Channel, boolean> = {
      email: true,
      push: true,
      inApp: true,
    };
    for (const pref of preferences) {
      for (const ch of CHANNELS) {
        const cp = pref[ch.key];
        // Only consider non-locked channels for master state
        if (!cp.locked && !cp.enabled) {
          channels[ch.key] = false;
        }
      }
    }
    return channels;
  }, [mode, preferences]);

  const handleUserMasterToggle = useCallback(
    (channel: Channel, value: boolean) => {
      if (!preferences || !onToggle) return;
      for (const pref of preferences) {
        const cp = pref[channel];
        if (!cp.locked && cp.enabled !== value) {
          onToggle(pref.notificationTypeId, channel, value);
        }
      }
    },
    [preferences, onToggle]
  );

  // --- Admin mode helpers ---

  const adminCategories = useMemo(() => {
    if (mode !== 'admin' || !companySettings) return [];
    return categorize(companySettings);
  }, [mode, companySettings]);

  /** Compute master toggle state for admin mode (inverted: enabled = !disabled) */
  const adminMasterState = useMemo(() => {
    if (mode !== 'admin' || !companySettings) {
      return { email: false, push: false, inApp: false };
    }
    const channels: Record<Channel, boolean> = {
      email: true,
      push: true,
      inApp: true,
    };
    for (const setting of companySettings) {
      if (setting.emailsDisabled) channels.email = false;
      if (setting.mobilePushNotificationsDisabled) channels.push = false;
      if (setting.inAppDisabled) channels.inApp = false;
    }
    return channels;
  }, [mode, companySettings]);

  const handleAdminMasterToggle = useCallback(
    (channel: Channel, value: boolean) => {
      if (!companySettings || !onCompanySettingChange) return;
      const field = adminFieldForChannel(channel);
      // value=true means enabled, so disabled=false
      for (const setting of companySettings) {
        const currentDisabled =
          setting[field as keyof CompanyNotificationSettingView] as boolean;
        const currentEnabled = !currentDisabled;
        if (currentEnabled !== value) {
          onCompanySettingChange(setting.notificationTypeId, field, !value);
        }
      }
    },
    [companySettings, onCompanySettingChange]
  );

  // --- Render helpers ---

  const getTypeLabel = (typeName: string): string => {
    const label = NOTIFICATION_TYPE_LABELS[typeName];
    return label ? t(label) : typeName;
  };

  const renderUserRow = (pref: NotificationPreferenceView) => {
    const allDisabled =
      !pref.email.enabled && !pref.push.enabled && !pref.inApp.enabled;

    return (
      <div
        key={pref.notificationTypeId}
        className="flex items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground truncate">
              {getTypeLabel(pref.notificationTypeName)}
            </span>
            {allDisabled && (
              <span
                className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1"
                title={t(
                  'No notifications will be delivered through any channel'
                )}
              >
                <AppIcon
                  name="AlertTriangle"
                  size={14}
                  className="shrink-0"
                />
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-6 sm:gap-8">
          {CHANNELS.map((ch) => {
            const cp = pref[ch.key];
            return (
              <div
                key={ch.key}
                className="flex items-center gap-1.5 w-12 justify-center"
              >
                <Switch
                  checked={cp.enabled}
                  onCheckedChange={(val) =>
                    onToggle?.(pref.notificationTypeId, ch.key, val)
                  }
                  disabled={disabled || readOnly || cp.locked}
                />
                {cp.locked && (
                  <AppIcon
                    name="Lock"
                    size={14}
                    className="text-muted-foreground shrink-0"
                    title={t('Disabled by your administrator')}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAdminRow = (setting: CompanyNotificationSettingView) => {
    return (
      <div
        key={setting.notificationTypeId}
        className="flex items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <span className="text-sm text-foreground truncate">
            {getTypeLabel(setting.notificationTypeName)}
          </span>
        </div>
        <div className="flex items-center gap-6 sm:gap-8">
          {CHANNELS.map((ch) => {
            const field = adminFieldForChannel(ch.key);
            const isDisabled =
              setting[
                field as keyof CompanyNotificationSettingView
              ] as boolean;
            // Switch ON = enabled (allowed), OFF = force-disabled
            return (
              <div
                key={ch.key}
                className="flex items-center w-12 justify-center"
              >
                <Switch
                  checked={!isDisabled}
                  onCheckedChange={(val) =>
                    onCompanySettingChange?.(
                      setting.notificationTypeId,
                      field,
                      !val
                    )
                  }
                  disabled={disabled || readOnly}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const categories =
    mode === 'user' ? userCategories : adminCategories;
  const masterState =
    mode === 'user' ? userMasterState : adminMasterState;
  const handleMasterToggle =
    mode === 'user' ? handleUserMasterToggle : handleAdminMasterToggle;

  if (categories.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col rounded-lg border border-border overflow-hidden">
      {/* Column headers + Master toggles */}
      <div className="flex items-center px-4 py-3 bg-muted/50 border-b border-border">
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {mode === 'user'
              ? t('Notification Preferences')
              : t('Notification Management')}
          </span>
        </div>
        <div className="flex items-center gap-6 sm:gap-8">
          {CHANNELS.map((ch) => (
            <div
              key={ch.key}
              className="flex flex-col items-center gap-1.5 w-12"
            >
              <span className="text-xs font-medium text-muted-foreground">
                {t(ch.label)}
              </span>
              <Switch
                checked={masterState[ch.key]}
                onCheckedChange={(val) => handleMasterToggle(ch.key, val)}
                disabled={disabled || readOnly}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Category sections */}
      {categories.map(({ category, items }) => {
        const isCollapsed = collapsedCategories.has(category);

        return (
          <div key={category}>
            {/* Category header */}
            <button
              type="button"
              onClick={() => toggleCategory(category)}
              className={cn(
                'flex items-center w-full px-4 py-2.5 bg-muted/30 border-b border-border',
                'hover:bg-muted/50 transition-colors cursor-pointer text-start'
              )}
            >
              <AppIcon
                name="ChevronRight"
                size={16}
                className={cn(
                  'text-muted-foreground transition-transform duration-200 shrink-0 ltr:mr-2 rtl:ml-2',
                  !isCollapsed && 'rotate-90'
                )}
              />
              <span className="text-sm font-medium text-primary">
                {t(category)}
              </span>
              <span className="text-xs text-muted-foreground ltr:ml-2 rtl:mr-2">
                ({items.length})
              </span>
            </button>

            {/* Category items */}
            {!isCollapsed && (
              <div>
                {items.map((item) =>
                  mode === 'user'
                    ? renderUserRow(item as NotificationPreferenceView)
                    : renderAdminRow(
                        item as CompanyNotificationSettingView
                      )
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default NotificationPreferenceMatrix;
